/**
 * 5-metric summary rail. Reads from the /v1/cascade summary.
 *
 * Clicking a metric HIGHLIGHTS the impacted nodes behind it on the canvas
 * (via the parent's `onMetric` -> the same highlight visual as search),
 * toggling off on a second click. These are cascade AGGREGATES, so they do
 * NOT open the per-entity provenance trace -- that lives on NODE clicks,
 * where the entity id is real. (The old handoff placeholder pointed every
 * metric at a fake `vendor/00000000-...` entity, which 404'd "no
 * blast-radius snapshot" once P94's deriver landed -- the deriver seals
 * real entities, never an aggregate.)
 */
import { useTranslation } from "react-i18next";

import { formatSARCompact } from "shared/money/money";
import type { CascadeSummary } from "shared/api/types";

export interface MetricsRailProps {
  cascade: CascadeSummary | null | undefined;
  onMetric: (metricKey: MetricKey) => void;
  active: MetricKey | null;
}

export type MetricKey =
  | "ibsImpacted"
  | "criticalFindings"
  | "revenueDaily"
  | "mvbIntersection"
  | "channelsDegraded";

export function MetricsRail({
  cascade,
  onMetric,
  active,
}: MetricsRailProps): JSX.Element {
  const { t } = useTranslation();
  const ibsImpacted = cascade?.ibs_impacted_count ?? 0;
  const criticals = countSeverity(cascade, "critical");
  const dailySar = parseDecimal(cascade?.total_daily_revenue_at_risk);
  const mvbHit = cascade?.mvb_impacted_count ?? 0;
  const mvbTotal = cascade?.mvb_total_count ?? 0;
  const mvbPct = mvbTotal > 0 ? Math.round((mvbHit / mvbTotal) * 100) : 0;
  const channels = cascade
    ? cascade.impacted.filter((i) => i.entity_kind === "channel").length
    : 0;
  /* Round 17 B11 -- green is a verdict. On an estate nothing has judged
     the five metrics read emerald over 0 under a banner saying no
     threshold colours the map; a zero there is the absence of a
     verdict, so it is drawn neutral. */
  const calm: "green" | "neutral" =
    !cascade || cascade.estate_assessed === false ? "neutral" : "green";

  return (
    <div
      data-testid="constellation-metrics-rail"
      className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-5"
    >
      <Metric
        label={t("modules.twin.constellation.metrics.ibsImpacted", {
          count: ibsImpacted,
        })}
        value={`${ibsImpacted}`}
        tone={ibsImpacted > 0 ? "red" : calm}
        testid="constellation-metric-ibs-impacted"
        active={active === "ibsImpacted"}
        onClick={() => onMetric("ibsImpacted")}
      />
      <Metric
        label={t("modules.twin.constellation.metrics.criticalFindings")}
        value={`${criticals}`}
        tone={criticals > 0 ? "red" : calm}
        testid="constellation-metric-critical-findings"
        active={active === "criticalFindings"}
        onClick={() => onMetric("criticalFindings")}
      />
      <Metric
        label={t("modules.twin.constellation.metrics.revenueDaily")}
        value={formatSARCompact(dailySar)}
        tone={dailySar > 0 ? "amber" : calm}
        testid="constellation-metric-revenue-daily"
        active={active === "revenueDaily"}
        onClick={() => onMetric("revenueDaily")}
      />
      <Metric
        label={t("modules.twin.constellation.metrics.mvbIntersection")}
        value={`${mvbPct}%`}
        sub={`${mvbHit}/${mvbTotal}`}
        tone={mvbHit > 0 ? "amber" : mvbTotal === 0 ? "neutral" : calm}
        testid="constellation-metric-mvb"
        active={active === "mvbIntersection"}
        onClick={() => onMetric("mvbIntersection")}
      />
      <Metric
        label={t("modules.twin.constellation.metrics.channelsDegraded")}
        value={`${channels}`}
        tone={channels > 0 ? "red" : calm}
        testid="constellation-metric-channels"
        active={active === "channelsDegraded"}
        onClick={() => onMetric("channelsDegraded")}
      />
    </div>
  );
}

interface MetricProps {
  label: string;
  value: string;
  sub?: string;
  tone: "red" | "amber" | "green" | "neutral";
  testid: string;
  active: boolean;
  onClick: () => void;
}

function Metric({
  label,
  value,
  sub,
  tone,
  testid,
  active,
  onClick,
}: MetricProps): JSX.Element {
  const { t } = useTranslation();
  const tones: Record<typeof tone, string> = {
    red: "text-rose-600",
    amber: "text-amber-600",
    green: "text-emerald-600",
    neutral: "text-slate-600",
  };
  return (
    <button
      type="button"
      data-testid={testid}
      data-active={active ? "true" : "false"}
      onClick={onClick}
      title={t("screens.metricsRail.highlightTheImpactedNodesOn")}
      className={`rounded border bg-white p-2 text-start shadow-sm focus-visible:outline-2 focus-visible:outline-brand-brass ${
        active
          ? "border-brand-brass ring-1 ring-brand-brass/40"
          : "border-slate-200 hover:border-brand-brass/40 hover:bg-brand-brass/5"
      }`}
    >
      <div className="flex items-baseline gap-1">
        <span className={`text-xl font-semibold tabular-nums ${tones[tone]}`}>
          {value}
        </span>
        {sub && <span className="text-xs text-slate-500">{sub}</span>}
      </div>
      <div className="text-xs uppercase tracking-wide text-slate-500">{label}</div>
    </button>
  );
}

function parseDecimal(s: string | undefined): number {
  if (!s) return 0;
  const n = parseFloat(s);
  return Number.isFinite(n) ? n : 0;
}

function countSeverity(
  cascade: CascadeSummary | null | undefined,
  sev: "critical" | "high",
): number {
  if (!cascade) return 0;
  return cascade.impacted_by_severity[sev] ?? 0;
}
